<template>
  <Layout>
    <PageHeader :title="$t('module.event')" :items="breadcrumbs" />
    <b-alert v-if="!canRead" show variant="info">{{
      $t("label.eventAccessDenied")
    }}</b-alert>
    <div v-else class="card">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-lg-4 mb-2 mb-lg-0">
            <b-input-group>
              <b-form-input
                v-model.trim="searchText"
                type="search"
                :placeholder="$t('label.search')"
                @keyup.enter="applySearch"
              />
              <b-input-group-append
                ><b-button
                  variant="info"
                  :aria-label="$t('label.search')"
                  @click="applySearch"
                  ><i class="mdi mdi-magnify"></i></b-button
              ></b-input-group-append>
            </b-input-group>
          </div>
          <div class="col-lg-8 d-flex justify-content-lg-end">
            <b-button
              v-if="canWrite"
              variant="info"
              @click="openForm(null, 'create')"
              ><i class="mdi mdi-plus mr-1"></i
              >{{ $t("label.createX", [$t("module.event")]) }}</b-button
            >
          </div>
        </div>
        <div v-if="!status.list" class="text-center p-5">
          <b-spinner variant="info" />
        </div>
        <b-alert v-else-if="listError" show variant="danger"
          >{{ $t("label.eventLoadError") }}
          <b-button size="sm" variant="outline-danger" @click="loadEvents">{{
            $t("label.retryPermissionDefinitions")
          }}</b-button></b-alert
        >
        <template v-else>
          <b-table
            :items="list.data"
            :fields="fields"
            primary-key="uuid"
            responsive
            show-empty
          >
            <template #cell(name)="{ item }"
              ><div class="d-flex align-items-center">
                <img
                  v-if="item.coverImage"
                  :src="item.coverImage"
                  alt=""
                  class="event-thumbnail mr-2"
                /><span>{{ item.name }}</span>
              </div></template
            >
            <template #cell(company)="{ item }"><CompanyLink :company="item.company" :fallback="$t('label.eventAllUsers')" /></template>
            <template #cell(venue)="{ item }"
              ><div>{{ venueLabel(item.venueType) }}</div>
              <small class="text-muted">{{ item.venueName }}</small></template
            >
            <template #cell(isActive)="{ item }">
              <b-form-checkbox
                :key="`${item.uuid}-${toggleVersion}`"
                :checked="item.isActive"
                switch
                :disabled="!canWrite || mutationPending || !status.save"
                @change="confirmActive(item, $event)"
                >{{
                  $t(item.isActive ? "label.active" : "label.inactive")
                }}</b-form-checkbox
              >
            </template>
            <template #cell(actions)="{ item }">
              <b-button
                variant="link"
                class="p-0 mr-2"
                :title="$t('label.eventUsers')"
                :aria-label="$t('label.eventUsers')"
                @click="openUsers(item)"
                ><i class="mdi mdi-account-multiple-outline font-size-18"></i
              ></b-button>
              <b-button
                variant="link"
                class="p-0 mr-2"
                :disabled="mutationPending || !status.save"
                :title="
                  canWrite
                    ? $t('label.updateX', [$t('module.event')])
                    : $t('label.eventView')
                "
                :aria-label="
                  canWrite
                    ? $t('label.updateX', [$t('module.event')])
                    : $t('label.eventView')
                "
                @click="openForm(item)"
                ><i
                  class="mdi font-size-18"
                  :class="canWrite ? 'mdi-pencil-outline' : 'mdi-eye-outline'"
                ></i
              ></b-button>
              <b-button
                v-if="canWrite"
                variant="link"
                class="text-danger p-0"
                :disabled="mutationPending || !status.save"
                :title="$t('label.delete')"
                :aria-label="$t('label.delete')"
                @click="confirmDelete(item)"
                ><i class="mdi mdi-delete-outline font-size-18"></i
              ></b-button>
            </template>
          </b-table>
          <div
            v-if="list.pagination.total"
            class="d-flex flex-wrap justify-content-between align-items-center mt-3"
          >
            <small class="text-muted">{{
              $tc("label.results", list.pagination.total, [
                list.pagination.total,
              ])
            }}</small>
            <b-pagination
              :value="criteria.pageNumber"
              :total-rows="list.pagination.total"
              :per-page="list.pagination.pageSize || criteria.pageSize"
              class="mb-0"
              @change="changePage"
            />
          </div>
        </template>
      </div>
    </div>
    <b-modal
      id="event-form"
      :title="modalTitle"
      size="lg"
      hide-footer
      centered
      :no-close-on-backdrop="!status.save"
      :no-close-on-esc="!status.save"
      :hide-header-close="!status.save"
      @hidden="formVisible = false"
    >
      <EventForm
        v-if="formVisible && canRead"
        :key="`${selectedUUID}-${formMode}`"
        :event-u-u-i-d="selectedUUID"
        :read-only="formMode === 'view' || !canWrite"
        @done="closeForm"
        @saved="saved"
      />
    </b-modal>
    <b-modal
      id="event-users"
      :title="$t('label.eventUsers')"
      size="xl"
      hide-footer
      centered
      @hidden="usersVisible = false"
    >
      <EventUsers
        v-if="usersVisible && canRead"
        :key="usersEvent.uuid"
        :event-u-u-i-d="usersEvent.uuid"
        :event-name="usersEvent.name"
      />
    </b-modal>
  </Layout>
</template>
<script>
import { mapActions, mapState } from "vuex";
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import EventForm from "./form.vue";
import EventUsers from "./users.vue";
import { sessionHelper } from "@/utils";
import i18n from "@/i18n";
export default {
  name: "EventListing",
  page: { title: i18n.t("module.event") },
  components: { Layout, PageHeader, EventForm, EventUsers },
  data() {
    return {
      searchText: "",
      criteria: { pageNumber: 1, pageSize: 15, searchArray: [] },
      selectedUUID: "",
      formMode: "view",
      formVisible: false,
      mutationPending: false,
      toggleVersion: 0,
      usersVisible: false,
      usersEvent: { uuid: "", name: "" },
      breadcrumbs: [
        { text: i18n.t("module.communication"), href: "/communications" },
        { text: i18n.t("module.event"), active: true },
      ],
    };
  },
  computed: {
    ...mapState("api/event", ["list", "status", "listError"]),
    permission() {
      const user = this.$store.state.api.auth.user?.user;
      return (
        sessionHelper.getUserPermissions(user?.permissions, user?.isSuperadmin)
          .event_admin || 0
      );
    },
    canRead() {
      return this.permission >= 1;
    },
    canWrite() {
      return this.permission === 2;
    },
    modalTitle() {
      return this.formMode === "view"
        ? this.$t("label.eventView")
        : this.$t(
            this.formMode === "create" ? "label.createX" : "label.updateX",
            [this.$t("module.event")],
          );
    },
    fields() {
      return [
        {
          key: "name",
          label: this.$t("label.name"),
          class: "event-name-column",
        },
        { key: "company", label: this.$t("label.company") },
        { key: "venue", label: this.$t("label.eventVenue") },
        {
          key: "startedAtDisplay",
          label: this.$t("label.eventStartedAt"),
          class: "text-nowrap",
        },
        {
          key: "endedAtDisplay",
          label: this.$t("label.eventEndedAt"),
          class: "text-nowrap",
        },
        { key: "joinedCount", label: this.$t("label.eventJoinedCount") },
        {
          key: "isActive",
          label: this.$t("label.active"),
          class: "text-nowrap",
        },
        {
          key: "actions",
          label: this.$t("label.action"),
          class: "text-nowrap",
        },
      ];
    },
  },
  watch: {
    canRead(allowed) {
      if (!allowed) {
        this.closeForm();
        this.$bvModal.hide("event-users");
        this.usersVisible = false;
      }
      this.loadEvents();
    },
  },
  created() {
    this.loadEvents();
  },
  methods: {
    ...mapActions("api/event", [
      "getAllEvent",
      "updateEventActiveStatus",
      "deleteEvent",
    ]),
    loadEvents() {
      return this.getAllEvent({ filter: this.criteria });
    },
    applySearch() {
      this.criteria = {
        ...this.criteria,
        pageNumber: 1,
        searchArray: this.searchText ? [this.searchText] : [],
      };
      return this.loadEvents();
    },
    changePage(pageNumber) {
      this.criteria = { ...this.criteria, pageNumber };
      return this.loadEvents();
    },
    venueLabel(type) {
      return [0, 1, 2].includes(type)
        ? this.$t(
            ["label.eventVirtual", "label.eventHybrid", "label.eventPhysical"][
              type
            ],
          )
        : "—";
    },
    openUsers(event) {
      if (!event?.uuid || sessionHelper.getPermissionLevel("event_admin") < 1)
        return;
      this.usersEvent = { uuid: event.uuid, name: event.name };
      this.usersVisible = true;
      this.$nextTick(() => this.$bvModal.show("event-users"));
    },
    openForm(event, mode) {
      if (!mode)
        mode =
          sessionHelper.getPermissionLevel("event_admin") === 2
            ? "edit"
            : "view";
      if (
        sessionHelper.getPermissionLevel("event_admin") <
        (mode === "view" ? 1 : 2)
      )
        return;
      this.selectedUUID = event?.uuid || "";
      this.formMode = mode;
      this.formVisible = true;
      this.$nextTick(() => this.$bvModal.show("event-form"));
    },
    closeForm() {
      this.$bvModal.hide("event-form");
      this.formVisible = false;
    },
    saved() {
      this.closeForm();
      this.loadEvents();
    },
    async refreshAfterMutation() {
      const result = await this.loadEvents();
      if (
        result?.success &&
        !this.list.data.length &&
        this.criteria.pageNumber > 1
      )
        await this.changePage(this.criteria.pageNumber - 1);
    },
    async confirmActive(event, isActive) {
      if (this.mutationPending || !this.status.save) return;
      if (sessionHelper.getPermissionLevel("event_admin") !== 2) {
        this.toggleVersion++;
        return;
      }
      this.mutationPending = true;
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t("label.confirmChangeStatusX", [
            this.$t(isActive ? "label.activate" : "label.deactivate"),
            event.name,
          ]),
          {
            title: this.$t("label.confirmStatusChange"),
            okTitle: this.$t("label.confirm"),
            cancelTitle: this.$t("label.cancel"),
            okVariant: "info",
            centered: true,
          },
        );
        if (!confirmed || sessionHelper.getPermissionLevel("event_admin") !== 2)
          return;
        const result = await this.updateEventActiveStatus({
          uuid: event.uuid,
          isActive,
        });
        if (result?.success) await this.refreshAfterMutation();
      } finally {
        this.toggleVersion++;
        this.mutationPending = false;
      }
    },
    async confirmDelete(event) {
      if (
        this.mutationPending ||
        !this.status.save ||
        sessionHelper.getPermissionLevel("event_admin") !== 2
      )
        return;
      this.mutationPending = true;
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t("label.confirmDeleteX", [event.name]),
          {
            title: this.$t("label.delete"),
            okTitle: this.$t("label.delete"),
            cancelTitle: this.$t("label.cancel"),
            okVariant: "danger",
            centered: true,
          },
        );
        if (!confirmed || sessionHelper.getPermissionLevel("event_admin") !== 2)
          return;
        const result = await this.deleteEvent({ uuid: event.uuid });
        if (result?.success) await this.refreshAfterMutation();
      } finally {
        this.mutationPending = false;
      }
    },
  },
};
</script>
<style scoped>
.event-thumbnail {
  width: 48px;
  height: 36px;
  object-fit: cover;
  border-radius: 4px;
}
::v-deep .event-name-column {
  min-width: 180px;
  max-width: 320px;
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
